// React
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { Link } from 'react-router-dom';

// Utils
import cn from '@/utils/cn';

// Components
import Picto from '@/components/atoms/Picto/Picto';

// Types
import type { HeaderMenuType, HeaderType } from '../HeaderType';

// Styles
import styles from './BurgerMenu.module.css';

interface BurgerMenuProps {
  menus?: HeaderType['menus'];
  isHeaderScrolled: boolean;
}

const burgerPaths = [
  // eslint-disable-next-line max-len
  'm 30,33 h 40 c 13.100415,0 14.380204,31.80258 6.899646,33.421777 -24.612039,5.327373 9.016154,-52.337577 -12.75751,-30.563913 l -28.284272,28.284272',
  // eslint-disable-next-line max-len
  'm 70,50 c 0,0 -32.213436,0 -40,0 -7.786564,0 -6.428571,-4.640244 -6.428571,-8.571429 0,-5.895471 6.073743,-11.783399 12.286435,-5.570707 6.212692,6.212692 28.284272,28.284272 28.284272,28.284272',
  // eslint-disable-next-line max-len
  'm 69.575405,67.073826 h -40 c -13.100415,0 -14.380204,-31.80258 -6.899646,-33.421777 24.612039,-5.327373 -9.016154,52.337577 12.75751,30.563913 l 28.284272,-28.284272',
] as const;

/** Écart entre le bas du header et le haut du panneau (px) */
const DROPDOWN_GAP_PX = 8;

function hasSubmenu(entry: HeaderMenuType): boolean {
  const groups = entry.dropdown?.linkGroup;
  return Boolean(groups && groups.length > 0);
}

function BurgerMenu({ menus = null, isHeaderScrolled }: BurgerMenuProps) {
  const dropdownId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [isOpened, setIsOpened] = useState(false);
  const [subMenuEntry, setSubMenuEntry] = useState<HeaderMenuType | null>(null);
  const [dropdownTopPx, setDropdownTopPx] = useState<number | null>(null);

  const filteredMenus = useMemo(() => (menus ?? []).filter((entry) => entry.link || entry.dropdown), [menus]);

  const closeBurger = useCallback(() => {
    setIsOpened(false);
    setSubMenuEntry(null);
  }, []);

  const openBurger = useCallback(() => {
    setSubMenuEntry(null);
    setIsOpened(true);
  }, []);

  const toggleBurger = useCallback(() => {
    if (isOpened) {
      closeBurger();
    } else {
      openBurger();
    }
  }, [isOpened, closeBurger, openBurger]);

  useEffect(() => {
    if (!isOpened) return undefined;

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') closeBurger();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpened, closeBurger]);

  useEffect(() => {
    const smoother = ScrollSmoother.get();
    if (smoother) {
      smoother.paused(isOpened);
    } else if (isOpened) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpened]);

  useLayoutEffect(() => {
    if (!isOpened) {
      setDropdownTopPx(null);
      return undefined;
    }

    const el = wrapperRef.current;
    if (!el) return undefined;

    const header = el.closest('header');
    if (!header) return undefined;

    /** Barre pill (logo + burger), pas le CTA mobile éventuel sous le bloc */
    const headerBar = header.firstElementChild as HTMLElement | null;
    if (!headerBar) return undefined;

    const updateTop = () => {
      const { bottom } = headerBar.getBoundingClientRect();
      setDropdownTopPx(bottom + DROPDOWN_GAP_PX);
    };

    updateTop();

    const ro = new ResizeObserver(updateTop);
    ro.observe(headerBar);
    window.addEventListener('resize', updateTop);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateTop);
    };
  }, [isOpened]);

  const renderMainMenuItem = (entry: HeaderMenuType) => {
    const highlight = entry.highlight && styles.mainLinkHighlight;

    if (entry.link) {
      return (
        <Link className={cn([styles.mainLink, highlight])} to={entry.link} onClick={closeBurger}>
          {entry.label}
        </Link>
      );
    }

    if (hasSubmenu(entry)) {
      return (
        <button
          type="button"
          className={cn([styles.mainLink, styles.mainLinkButton, highlight])}
          onClick={() => setSubMenuEntry(entry)}
        >
          <span className={styles.mainLinkLabel}>{entry.label}</span>
          <Picto icon="arrowRight" width={14} className={styles.mainLinkIcon} aria-hidden />
        </button>
      );
    }

    return <span className={cn([styles.mainLink, styles.mainLeaf, highlight])}>{entry.label}</span>;
  };

  const dropdownPanelStyle =
    dropdownTopPx !== null
      ? {
        top: dropdownTopPx,
        maxHeight: `calc(100vh - ${dropdownTopPx}px - var(--spacing-m))`,
      }
      : undefined;

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <div
        id={dropdownId}
        className={cn([styles.dropdownRoot, isOpened && styles.dropdownRootOpen])}
        aria-hidden={!isOpened}
      >
        <button type="button" className={styles.backdrop} onClick={closeBurger} aria-label="Fermer le menu" />
        <div className={styles.dropdownPanel} style={dropdownPanelStyle}>
          <div className={styles.sliderViewport}>
            <div className={cn([styles.sliderTrack, subMenuEntry && styles.sliderTrackSub])}>
              <div className={styles.level}>
                <ul className={styles.mainList}>
                  {filteredMenus.map((entry) => (
                    <li key={entry.id} className={styles.mainItem}>
                      {renderMainMenuItem(entry)}
                    </li>
                  ))}
                </ul>
              </div>

              <div className={styles.level}>
                {subMenuEntry?.dropdown?.linkGroup && (
                  <div className={styles.subPanel}>
                    <button type="button" className={styles.backRow} onClick={() => setSubMenuEntry(null)}>
                      <Picto icon="arrowLeft" width={14} className={styles.backIcon} aria-hidden />
                      <span className={styles.backLabel}>{subMenuEntry.label}</span>
                    </button>
                    <div className={styles.subContent}>
                      {subMenuEntry.dropdown.linkGroup
                        .filter((group) => (group.links?.length ?? 0) > 0)
                        .map((group) => (
                          <div key={group.id} className={styles.linkGroup}>
                            {group.label ? <h3 className={styles.linkGroupTitle}>{group.label}</h3> : null}
                            <ul className={styles.linkGroupList}>
                              {(group.links ?? []).map((link) => (
                                <li key={`${link.label}-${link.link}`} className={styles.subLinkItem}>
                                  <Picto icon="arrowRight" width={14} className={styles.subLinkIcon} aria-hidden />
                                  <Link className={styles.subLink} to={link.link} onClick={closeBurger}>
                                    {link.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        className={cn([styles.burger, isOpened ? styles.close : '', isHeaderScrolled ? styles.scrolled : ''])}
        aria-label="menu"
        aria-expanded={isOpened}
        aria-controls={dropdownId}
        onClick={toggleBurger}
      >
        <svg className={cn([styles.bars, isOpened ? styles.cross : ''])} viewBox="0 0 100 100">
          <path className={cn([styles.line, styles.top])} d={burgerPaths[0]} />
          <path className={cn([styles.line, styles.middle])} d={burgerPaths[1]} />
          <path className={cn([styles.line, styles.bottom])} d={burgerPaths[2]} />
        </svg>
      </button>
    </div>
  );
}

export default BurgerMenu;
